import { useState } from "react";
import {
  KeyRound,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";

/**
 * Admin Account page — currently exposes a single primary action: change
 * password. Lives under /admin/account inside the protected admin shell.
 *
 * Backend: POST /api/admin/auth/password-change
 *   body: { current_password, new_password }
 *   201/200 → success (cookie quietly rotated server-side)
 *   401     → current password is wrong
 *   400     → admin has no password (Google-only flow)
 *
 * Behaviour notes:
 *  - On success we DO NOT log the admin out — the backend re-issues a
 *    fresh session cookie atomically so the page they're working in stays
 *    usable.
 *  - All other admin sessions for the same user are revoked server-side
 *    so a leaked cookie can't outlive the rotation.
 */

const MIN_NEW_PASSWORD = 8;

export default function AdminAccount() {
  const { admin } = useAdminAuth();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const canSubmit =
    !submitting &&
    current.trim().length > 0 &&
    next.length >= MIN_NEW_PASSWORD &&
    next === confirmPw;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!current) {
      setError("Please enter your current password.");
      return;
    }
    if (next.length < MIN_NEW_PASSWORD) {
      setError(`New password must be at least ${MIN_NEW_PASSWORD} characters.`);
      return;
    }
    if (next === current) {
      setError("Your new password must be different from the current one.");
      return;
    }
    if (next !== confirmPw) {
      setError("New password and confirmation do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/admin/auth/password-change`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: current,
          new_password: next,
        }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        if (res.status === 401) {
          setError("Current password is incorrect.");
        } else if (res.status === 400) {
          setError(
            (data && data.detail) ||
              "Password change is not available for this admin account."
          );
        } else if (res.status === 422) {
          setError(`New password must be at least ${MIN_NEW_PASSWORD} characters.`);
        } else {
          setError(
            (data && (data.detail || data.message)) ||
              "Could not update password. Please try again."
          );
        }
        return;
      }
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirmPw("");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-account-page" className="max-w-2xl">
      <div className="sas-eyebrow mb-2">Account</div>
      <h1 className="sas-h1" data-testid="admin-account-heading">
        Account &amp; security
      </h1>
      <p className="sas-body mt-3 text-[15px]">
        Manage credentials for your admin sign-in. Changes take effect
        immediately and revoke any other open admin sessions on this account.
      </p>

      <section
        className="mt-8 rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6"
        data-testid="admin-account-identity"
      >
        <h2 className="text-[13px] font-semibold text-[color:var(--sas-ink)] uppercase tracking-wide">
          Signed in as
        </h2>
        <p className="sas-body mt-2 text-[14px]">
          <span className="font-medium text-[color:var(--sas-ink)]">
            {admin?.name || admin?.email?.split("@")[0] || "Admin"}
          </span>
          <span className="mx-2 opacity-50" aria-hidden>
            ·
          </span>
          <span data-testid="admin-account-email">{admin?.email}</span>
        </p>
        <p className="text-xs text-[color:var(--sas-ink-2)] mt-2">
          Sessions last 7 days. Every request is verified server-side.
        </p>
      </section>

      <section
        className="mt-6 rounded-xl border border-[color:var(--sas-line)] bg-white p-5 md:p-6"
        data-testid="admin-account-password-card"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
            aria-hidden
          >
            <KeyRound size={18} />
          </div>
          <div>
            <h2 className="text-[15.5px] font-semibold text-[color:var(--sas-ink)]">
              Change password
            </h2>
            <p className="sas-body mt-1 text-[13.5px]">
              Use a strong password (at least {MIN_NEW_PASSWORD} characters).
              You will stay signed in on this device after the change.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
          {error && (
            <div
              className="flex items-start gap-2 rounded-lg border border-[color:#fecaca] bg-[#fef2f2] px-3 py-2.5 text-[13px] text-[#991b1b]"
              role="alert"
              data-testid="admin-account-error"
            >
              <AlertCircle size={15} className="mt-0.5 shrink-0" aria-hidden />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div
              className="flex items-start gap-2 rounded-lg border border-[color:#bbf7d0] bg-[#f0fdf4] px-3 py-2.5 text-[13px] text-[#14532d]"
              role="status"
              data-testid="admin-account-success"
            >
              <CheckCircle2 size={15} className="mt-0.5 shrink-0" aria-hidden />
              <span>Password updated successfully.</span>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="admin-current-password"
              className="text-[12.5px] font-semibold text-[color:var(--sas-ink)]"
            >
              Current password
            </label>
            <input
              id="admin-current-password"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="sas-input"
              placeholder="Enter your current password"
              data-testid="admin-account-current-password"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="admin-new-password"
              className="text-[12.5px] font-semibold text-[color:var(--sas-ink)]"
            >
              New password
            </label>
            <div className="relative">
              <input
                id="admin-new-password"
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={MIN_NEW_PASSWORD}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="sas-input pr-10"
                placeholder={`At least ${MIN_NEW_PASSWORD} characters`}
                data-testid="admin-account-new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute end-2 top-1/2 -translate-y-1/2 p-1 text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)]"
                aria-label={showNew ? "Hide password" : "Show password"}
                data-testid="admin-account-toggle-show-new"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div className="text-[11.5px] text-[color:var(--sas-ink-2)]">
              {next.length === 0
                ? `Minimum ${MIN_NEW_PASSWORD} characters.`
                : next.length < MIN_NEW_PASSWORD
                ? `Too short — needs ${MIN_NEW_PASSWORD - next.length} more character${
                    MIN_NEW_PASSWORD - next.length === 1 ? "" : "s"
                  }.`
                : "Length looks good."}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="admin-confirm-password"
              className="text-[12.5px] font-semibold text-[color:var(--sas-ink)]"
            >
              Confirm new password
            </label>
            <input
              id="admin-confirm-password"
              type={showNew ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="sas-input"
              placeholder="Re-enter your new password"
              data-testid="admin-account-confirm-password"
            />
            {confirmPw.length > 0 && confirmPw !== next && (
              <div
                className="text-[11.5px] text-[#991b1b]"
                data-testid="admin-account-confirm-mismatch"
              >
                Confirmation does not match the new password.
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className="sas-btn sas-btn-primary justify-center"
              data-testid="admin-account-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden />
                  Updating…
                </>
              ) : (
                <>
                  <KeyRound size={16} aria-hidden />
                  Update password
                </>
              )}
            </button>
            <span className="text-[11.5px] text-[color:var(--sas-ink-2)]">
              Other admin sessions on this account will be signed out.
            </span>
          </div>
        </form>
      </section>
    </div>
  );
}
